import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import MemberService from "../../../services/member_service";
import GamificationService from "../../../services/gamification_service";

export const mapBackendToFrontend = (backendStatus) => {
    if (backendStatus === "ATTENDED") return "present";
    if (backendStatus === "NO_SHOW") return "absent";
    return "present";
};

export const mapFrontendToBackend = (frontendStatus) => {
    if (frontendStatus === "present") return "ATTENDED";
    if (frontendStatus === "absent") return "NO_SHOW";
    return "BOOKED";
};

export const useAttendanceData = ({ isOpen, schedule, idTenant }) => {
    const [members, setMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [gamificationSettings, setGamificationSettings] = useState(null);

    useEffect(() => {
        if (isOpen && schedule?.attendances) {
            const fetchAttendanceDetails = async () => {
                setLoadingMembers(true);
                try {
                    const attendances = schedule.attendances;

                    if (!attendances.length) {
                        setMembers([]);
                        return;
                    }

                    const memberPromises = attendances.map(async (att) => {
                        const memberDetails = await MemberService.getMemberById(idTenant, att.idMember);

                        return {
                            id: att.idMember,
                            name: memberDetails?.fullName || att.memberName,
                            photo: memberDetails?.photoUrl,
                            phone: att.memberPhone || memberDetails?.phone || "",
                            email: att.memberEmail || memberDetails?.email || "",
                            memberStatus: memberDetails?.status,
                            idMember: memberDetails?.idMember,
                            enrollmentType: att.type,
                            idEnrollment: att.idEnrollment,
                            justification: att.justification || "",
                            status: mapBackendToFrontend(att.status),
                            initialFrontendStatus: mapBackendToFrontend(att.status),
                            oldStatus: att.status,
                            oldJustification: att.justification || "",
                        };
                    });

                    const resolvedMembers = await Promise.all(memberPromises);

                    // Filtro de segurança UI: se for sessão de hoje em diante, oculta alunos inativos
                    // (O backend já devia ter limpado, mas isso previne fantasmas que não foram filtrados a tempo)
                    const todayStr = new Date().toISOString().split('T')[0];
                    let finalMembers = resolvedMembers;

                    if (schedule?.sessionDate && schedule.sessionDate >= todayStr) {
                        finalMembers = resolvedMembers.filter(m => {
                            const isExperimental = ['experimental', 'single-session', 'trial'].includes(String(m.enrollmentType || '').toLowerCase());
                            return m.memberStatus === 'active' || m.memberStatus === 'suspended' || isExperimental;
                        });
                    }

                    // Ordenar alfabeticamente pelo nome
                    finalMembers.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));

                    setMembers(finalMembers);
                } catch (error) {
                    console.error("[useAttendanceData] Erro ao carregar detalhes dos alunos:", error);
                    toast.error("Erro ao carregar lista de alunos.");
                } finally {
                    setLoadingMembers(false);
                }
            };

            fetchAttendanceDetails();

            GamificationService.getSettings(idTenant)
                .then(settings => setGamificationSettings(settings))
                .catch(() => setGamificationSettings(null));
        } else if (!isOpen) {
            setMembers([]);
        }
    }, [isOpen, schedule, idTenant]);

    const attendanceStats = useMemo(() => {
        const present = members.filter(c => c.status === "present").length;
        const absent = members.filter(c => c.status === "absent").length;
        return { present, absent, total: members.length };
    }, [members]);

    const isDirty = useMemo(() => {
        return members.some(c => {
            const currentBackendStatus = mapFrontendToBackend(c.status);
            return currentBackendStatus !== c.oldStatus || c.justification !== c.oldJustification;
        });
    }, [members]);

    const hasManualChanges = useMemo(() => {
        return members.some(c => {
            return c.status !== c.initialFrontendStatus || c.justification !== c.oldJustification;
        });
    }, [members]);

    const handleMarkAbsence = useCallback((id) => {
        setMembers(prev => prev.map(c => (c.id === id ? { ...c, status: "absent", justification: "" } : c)));
    }, []);

    const handleMarkPresent = useCallback((id) => {
        setMembers(prev => prev.map(c => (c.id === id ? { ...c, status: "present", justification: "" } : c)));
    }, []);

    const handleChangeJustification = useCallback((id, justification) => {
        setMembers(prev => prev.map(c => (c.id === id ? { ...c, justification } : c)));
    }, []);

    return {
        members,
        setMembers,
        loadingMembers,
        gamificationSettings,
        attendanceStats,
        isDirty,
        hasManualChanges,
        handleMarkAbsence,
        handleMarkPresent,
        handleChangeJustification
    };
};
